import { BreadcrumbSection, GUT_BODY_CONNECTIONS_COPY } from '@repo/ui';

const KIT_ID_REGEX =
  /\b(?:[1-2]\d{3}[ABC]\d{3}|[A-Z]{3}\d{3}(?:-\d+)?(?:MGI|A)?|KIT\d{3,5}|TESTKIT-\d{4}-\d{4}-\d{4}-\d{12}|temp_kit_id_\d+|TST\d{3,4}|SG\d{2}|CT\d{6}|KT\d{8}|[a-f0-9]{27}|UHYH\d{12}|YHDH\d{12}|unused_kit_\d+_\w+|KT_\d{13})\b/i;

interface RoutePattern {
  pattern: string | ((segment: string) => boolean);

  label: string | ((match: RouteMatch) => string);

  buildHref: (match: RouteMatch, segments: string[]) => string;

  when?: (match: RouteMatch, segments: string[]) => boolean;

  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
}

interface RouteMatch {
  params: Record<string, string>;
  segment: string;
  index: number;
  overridenLabel?: string;
  gutBodyConnections: boolean;
}

const ROUTE_PATTERNS: RoutePattern[] = [
  {
    pattern: 'results',
    label: 'Results',
    buildHref: ({ params }) => `/results/${params.kitId}`,
  },
  {
    // Category-scoped detail pages have no all-metrics segment to label
    pattern: 'metrics',
    label: 'All metrics',
    buildHref: ({ params }) => `/results/${params.kitId}/metrics/all-metrics`,
    when: ({ index }, segments) =>
      segments[index - 2] === 'results' &&
      segments[index + 1] !== 'all-metrics' &&
      !isNaN(+segments[index + 2]),
  },
  {
    pattern: 'all-metrics',
    label: ({ overridenLabel }) => overridenLabel ?? 'All metrics',
    buildHref: ({ params }) => `/results/${params.kitId}/metrics/all-metrics`,
    when: ({ index }, segments) => segments[index - 1] === 'metrics',
  },
  {
    pattern: s => !isNaN(+s),
    label: ({ overridenLabel, segment }) =>
      overridenLabel ?? `Metric #${segment}`,
    buildHref: ({ params, segment, index }, segments) =>
      `/results/${params.kitId}/metrics/${segments[index - 1]}/${segment}`,
    when: ({ index }, segments) =>
      segments[index - 2] === 'metrics' && segments[index - 4] === 'results',
  },
  {
    pattern: 'conditions',
    label: ({ overridenLabel, gutBodyConnections }) =>
      overridenLabel ??
      (gutBodyConnections
        ? GUT_BODY_CONNECTIONS_COPY.title
        : 'Health associations'),
    buildHref: ({ params }) => `/results/${params.kitId}/conditions/metrics`,
    when: ({ index }, segments) =>
      segments[index + 1] === 'metrics' ||
      segments[index + 1] === 'emerging-research' ||
      !isNaN(+segments[index + 1]),
  },
  {
    pattern: s => !!s,
    label: 'Emerging Research',
    buildHref: ({ params }) =>
      `/results/${params.kitId}/conditions/emerging-research`,
    when: ({ index, segment, gutBodyConnections }, segments) =>
      !gutBodyConnections &&
      (segment === 'emerging-research' ||
        (segments[index - 1] === 'conditions' && !isNaN(+segment))),
  },
  {
    pattern: s => !!s,
    label: ({ overridenLabel, segment }) => overridenLabel ?? segment,
    buildHref: ({ params, segment }) =>
      `/results/${params.kitId}/conditions/${segment}`,
    when: ({ index, segment }, segments) =>
      segments[index - 1] === 'conditions' && !isNaN(+segment),
  },
  {
    pattern: s => !!s,
    label: ({ segment, overridenLabel }) =>
      overridenLabel ?? `Condition #${segment}`,
    buildHref: ({ params, segment }) =>
      `/results/${params.kitId}/conditions/metrics/${segment}`,
    when: ({ index }, segments) =>
      segments[index - 2] === 'conditions' && segments[index - 1] === 'metrics',
  },
  {
    pattern: 'action-plan',
    label: 'Action Plan',
    buildHref: ({ params }) => `/action-plan/${params.kitId}`,
  },
  {
    pattern: s => !isNaN(+s),
    label: ({ overridenLabel, segment }) =>
      overridenLabel ?? `Action #${segment}`,
    buildHref: ({ params, segment }) =>
      `/action-plan/${params.kitId}/${segment}`,
    when: (_, segments) => segments[0] === 'action-plan',
  },
  {
    pattern: 'surveys',
    label: 'Surveys',
    buildHref: ({ params }) => `/results/${params.kitId}/surveys`,
  },
  {
    pattern: 'seeding',
    label: 'Microbiome Seeding',
    buildHref: ({ params }) => `/results/${params.kitId}/seeding`,
  },
  {
    pattern: 'microbiome',
    label: 'Microbiome Breakdown',
    buildHref: ({ params }) => `/results/${params.kitId}/microbiome`,
  },
];

function isKitId(segment: string): boolean {
  return KIT_ID_REGEX.test(segment);
}

function matchesPattern(
  pattern: string | ((segment: string) => boolean),
  segment: string,
): boolean {
  if (typeof pattern === 'function') {
    return pattern(segment);
  }
  return pattern === segment;
}

export default function breadcrumbResultsPortalParser({
  overrides,
  gutBodyConnections = false,
}: {
  overrides?: Record<string, string | undefined>;
  gutBodyConnections?: boolean;
}): BreadcrumbSection[] {
  const location = window?.location?.pathname ?? '';
  const urlSegments = location.split('/').filter(Boolean);

  if (urlSegments.length === 0) return [];

  const params: Record<string, string> = {};

  const kitIdIndex = urlSegments.findIndex(isKitId);
  if (kitIdIndex !== -1) {
    params.kitId = urlSegments[kitIdIndex];
  }

  const sections: BreadcrumbSection[] = [];

  urlSegments.forEach((segment, index) => {
    for (const route of ROUTE_PATTERNS) {
      if (!matchesPattern(route.pattern, segment)) {
        continue;
      }

      const overridenLabel = overrides?.[segment];

      const match: RouteMatch = {
        params,
        segment,
        index,
        overridenLabel,
        gutBodyConnections,
      };

      if (route.when && !route.when(match, urlSegments)) {
        continue;
      }

      const label =
        typeof route.label === 'function' ? route.label(match) : route.label;

      const href = route.buildHref(match, urlSegments);

      sections.push({ label, href, icon: route.icon });
    }
  });

  return sections;
}
